import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import type { FilterDefinition } from "@/shared/components/filters/FilterDefinition";
import { useCustomFieldValueSuggestions } from "@/shared/components/filters/useSuggestionSources";
import { TargetEntityType } from "@/shared/domain/customFields";
import { useReadyCustomFields } from "@/shared/domain/customFields/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import type {
  ColDef,
  ColumnPickerOption,
} from "@/shared/foundation/primitives/Table";

import {
  buildCustomFieldColumns,
  buildCustomFieldFilterDefinitions,
  buildCustomFieldColumnPickerOptions,
  computeEffectiveColumnPrefs,
  customFieldNameFromColumnId,
  type RowWithCustomFields,
} from "./customFieldGridBuilders";

export interface UseCustomFieldGridIntegrationParams {
  entityType: TargetEntityType;
  /** The grid's current column-id preference order. */
  preferenceOrder: readonly string[];
  /**
   * Persists the correction when a stored custom field is gone: `columns` is
   * `preferenceOrder` without the ids in `removedColumns`. A caller whose
   * `preferenceOrder` can come from somewhere other than what it stores
   * removes `removedColumns` from what it stores instead.
   */
  savePreferences: (next: {
    columns: string[];
    removedColumns: string[];
  }) => void;
}

export interface CustomFieldRemovedColumnsAlertState {
  /** Field names of since-deleted custom fields pruned from stored prefs. */
  fieldNames: string[];
  dismissed: boolean;
  dismiss: () => void;
}

export interface CustomFieldGridIntegration<TData extends RowWithCustomFields> {
  /** Custom-field filter definitions (place after core, before metadata). */
  filterDefinitions: FilterDefinition[];
  /** Custom-field columns (concat before metadata columns). */
  customFieldColumns: ColDef<TData>[];
  /** Column-picker options for the custom fields. */
  columnPickerOptions: ColumnPickerOption[];
  /** Column order with stale custom-field ids removed. */
  effectivePreferenceOrder: string[];
  /** Dismissible alert naming any auto-pruned, since-deleted custom fields. */
  removedColumnsAlert: CustomFieldRemovedColumnsAlertState;
}

/**
 * One-stop custom-field wiring for the entity grids: returns the filter
 * definitions, columns, column picker options, pruned column order, and removed-fields
 * alert a grid needs to render Ready custom fields. Used by every modern entity
 * grid.
 *
 * Behind that contract: fetches the Ready fields (gated on the
 * release-custom-fields flag) and auto-prunes a since-deleted custom field out
 * of stored column prefs — persisting the corrected order and surfacing a
 * dismissible alert — so a deleted field never leaves a broken column behind.
 */
export function useCustomFieldGridIntegration<
  TData extends RowWithCustomFields = RowWithCustomFields,
>({
  entityType,
  preferenceOrder,
  savePreferences,
}: UseCustomFieldGridIntegrationParams): CustomFieldGridIntegration<TData> {
  const flagEnabled = useCheckFeatureFlag("release-custom-fields");
  const orgId = useCurrentOrgId();
  const readyQuery = useReadyCustomFields({
    entityType,
    orgId,
    enabled: flagEnabled,
  });

  const readyFields = React.useMemo(
    () => (flagEnabled ? (readyQuery.data ?? []) : []),
    [flagEnabled, readyQuery.data],
  );

  // String fields suggest the values the org's entities already hold.
  const valueSuggestions = useCustomFieldValueSuggestions();
  const filterDefinitions = React.useMemo(
    () => buildCustomFieldFilterDefinitions(readyFields, "", valueSuggestions),
    [readyFields, valueSuggestions],
  );
  const customFieldColumns = React.useMemo(
    () => buildCustomFieldColumns<TData>(readyFields),
    [readyFields],
  );
  const pickerOptions = React.useMemo(
    () => buildCustomFieldColumnPickerOptions(readyFields),
    [readyFields],
  );

  const { prunedOrder, removedIds } = React.useMemo(
    () =>
      computeEffectiveColumnPrefs({
        flagEnabled,
        readyFieldsFetched: readyQuery.isSuccess,
        preferenceOrder,
        readyFields,
      }),
    [flagEnabled, readyQuery.isSuccess, preferenceOrder, readyFields],
  );

  const removedColumnsAlert = useRemovedColumnsAlert({
    removedIds,
    prunedOrder,
    savePreferences,
  });

  return {
    filterDefinitions,
    customFieldColumns,
    columnPickerOptions: pickerOptions,
    effectivePreferenceOrder: prunedOrder,
    removedColumnsAlert,
  };
}

interface UseRemovedColumnsAlertParams {
  /** `custom.<name>` ids dropped because their field is no longer Ready. */
  removedIds: string[];
  /** The column order with those ids already removed, to persist once. */
  prunedOrder: string[];
  savePreferences: UseCustomFieldGridIntegrationParams["savePreferences"];
}

/**
 * Reconciles stored column prefs against since-deleted custom fields: on the
 * first settle that surfaces new removed ids, persists the pruned order once
 * (ref-guarded so a re-render can't re-save) and accumulates the dropped field
 * names into a dismissible alert. Split out from the main hook because this
 * state and effect only come into play once a custom field has been pruned.
 */
function useRemovedColumnsAlert({
  removedIds,
  prunedOrder,
  savePreferences,
}: UseRemovedColumnsAlertParams): CustomFieldRemovedColumnsAlertState {
  const [removedFieldNames, setRemovedFieldNames] = React.useState<string[]>(
    [],
  );
  const [dismissed, setDismissed] = React.useState(false);
  const handledIdsRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    const freshRemovedIds = removedIds.filter(
      (id) => !handledIdsRef.current.has(id),
    );
    if (freshRemovedIds.length === 0) {
      return;
    }
    freshRemovedIds.forEach((id) => handledIdsRef.current.add(id));
    savePreferences({ columns: prunedOrder, removedColumns: removedIds });
    setRemovedFieldNames((previous) => [
      ...previous,
      ...freshRemovedIds.map(customFieldNameFromColumnId),
    ]);
    setDismissed(false);
  }, [removedIds, prunedOrder, savePreferences]);

  return React.useMemo<CustomFieldRemovedColumnsAlertState>(
    () => ({
      fieldNames: removedFieldNames,
      dismissed,
      dismiss: () => setDismissed(true),
    }),
    [removedFieldNames, dismissed],
  );
}
